import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  message: React.ReactNode;
  iconSize?: number | string;
  position?: "top" | "bottom" | "left" | "right" | "auto";
  delay?: number;
  className?: string;
  iconClassName?: string;
  variant?: "info" | "warning";
}

const Tooltip: React.FC<TooltipProps> = ({
  message,
  iconSize = 16,
  position = "auto",
  delay = 300,
  className = "",
  iconClassName = "",
  variant = "info"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [calculatedPosition, setCalculatedPosition] = useState<"top" | "bottom" | "left" | "right">("top");
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const iconStyle = {
    width: typeof iconSize === "number" ? `${iconSize}px` : iconSize,
    height: typeof iconSize === "number" ? `${iconSize}px` : iconSize
  };

  const calculateOptimalPosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check space available in each direction
    const spaceTop = triggerRect.top;
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    let pos: "top" | "bottom" | "left" | "right" = "top";
    let tooltipTop = 0;
    let tooltipLeft = 0;

    if (position !== "auto") {
      pos = position as "top" | "bottom" | "left" | "right";
    } else {
      // Prefer top/bottom positioning
      if (spaceTop > 80) {
        pos = "top";
      } else if (spaceBottom > 80) {
        pos = "bottom";
      } else if (spaceRight > 400) {
        pos = "right";
      } else if (spaceLeft > 400) {
        pos = "left";
      } else {
        // Default to top if no good option
        pos = "top";
      }
    }

    // Calculate absolute positioning based on trigger element
    const tooltipOffset = 12; // Distance from trigger element
    const padding = 16; // Padding from viewport edges

    // Calculate available width for tooltip
    let maxWidth = 800; // Default max width
    let tooltipWidth = Math.min(maxWidth, viewportWidth - padding * 2);

    switch (pos) {
      case "top":
        tooltipTop = triggerRect.top - tooltipOffset;
        tooltipLeft = triggerRect.left + triggerRect.width / 2;

        // Adjust horizontal position if tooltip would exceed viewport
        const halfWidth = tooltipWidth / 2;
        if (tooltipLeft - halfWidth < padding) {
          tooltipLeft = padding + halfWidth;
        } else if (tooltipLeft + halfWidth > viewportWidth - padding) {
          tooltipLeft = viewportWidth - padding - halfWidth;
        }
        break;

      case "bottom":
        tooltipTop = triggerRect.bottom + tooltipOffset;
        tooltipLeft = triggerRect.left + triggerRect.width / 2;

        // Adjust horizontal position if tooltip would exceed viewport
        const halfWidthBottom = tooltipWidth / 2;
        if (tooltipLeft - halfWidthBottom < padding) {
          tooltipLeft = padding + halfWidthBottom;
        } else if (tooltipLeft + halfWidthBottom > viewportWidth - padding) {
          tooltipLeft = viewportWidth - padding - halfWidthBottom;
        }
        break;

      case "left":
        tooltipTop = triggerRect.top + triggerRect.height / 2;
        tooltipLeft = triggerRect.left - tooltipOffset;
        tooltipWidth = Math.min(maxWidth, triggerRect.left - padding - tooltipOffset);
        break;

      case "right":
        tooltipTop = triggerRect.top + triggerRect.height / 2;
        tooltipLeft = triggerRect.right + tooltipOffset;
        tooltipWidth = Math.min(maxWidth, viewportWidth - triggerRect.right - padding - tooltipOffset);
        break;
    }

    setCalculatedPosition(pos);
    setTooltipStyle({
      position: "fixed",
      top: tooltipTop,
      left: tooltipLeft,
      maxWidth: `${tooltipWidth}px`,
      zIndex: 9999
    });
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      calculateOptimalPosition();
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        calculateOptimalPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const getTooltipClasses = () => {
    const baseClasses = `
      whitespace-normal bg-secondary-800 text-white text-sm rounded-lg px-6 py-1.5 shadow-lg
      transition-all duration-200 ease-out min-w-0 w-auto
      pointer-events-none border border-secondary-700
      ${isVisible ? "opacity-100 visible" : "opacity-0 invisible"}
    `;

    const transformClasses = {
      top: "transform -translate-x-1/2 -translate-y-full",
      bottom: "transform -translate-x-1/2",
      left: "transform -translate-x-full -translate-y-1/2",
      right: "transform -translate-y-1/2"
    };

    return `${baseClasses} ${transformClasses[calculatedPosition]}`.trim().replace(/\s+/g, " ");
  };

  const getArrowClasses = () => {
    const baseArrowClasses = "absolute w-2 h-2 bg-secondary-800 border border-secondary-700 transform rotate-45";

    const arrowPositionClasses = {
      top: "bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2",
      bottom: "top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
      left: "right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2",
      right: "left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
    };

    return `${baseArrowClasses} ${arrowPositionClasses[calculatedPosition]}`;
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <div
        ref={triggerRef}
        className="inline-flex items-center cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        tabIndex={0}
        role="button"
        aria-describedby="tooltip"
      >
        {variant === "warning" ? (
          <div
            className={`flex items-center justify-center rounded-full bg-warning-100 text-warning-600 hover:bg-warning-200 hover:text-warning-700 transition-colors duration-200 ${iconClassName}`}
            style={iconStyle}
          >
            <svg fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        ) : (
          <div
            className={`flex items-center justify-center rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 hover:text-primary-700 transition-colors duration-200 ${iconClassName}`}
            style={iconStyle}
          >
            <svg className="" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Tooltip Portal - renders outside any overflow-hidden containers */}
      {isVisible &&
        createPortal(
          <div ref={tooltipRef} className={getTooltipClasses()} style={tooltipStyle} id="tooltip" role="tooltip">
            {/* Arrow */}
            <div className={getArrowClasses()} />

            {/* Content */}
            <div className="relative z-10 break-words leading-relaxed whitespace-normal">
              <div className="block py-0.5">{message}</div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export { Tooltip };
